import React, { useEffect, useMemo, useState } from "react";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel,
  MetricCard, Grid2, Grid3, Grid4, BtnRow, BtnPrimary, BtnLight,
  InputLabel, FormInput, FormTextarea, HelpText, Tag,
} from "../components/ui";

export default function Module1SchoolMaster() {
  const {
    schoolMaster,
    module1Loading,
    module1LoadError,
    module1MissingFields,
    canEditModules1to5,
    loadSchoolMasterFromSources,
    saveSchoolMasterPriority,
    resetSchoolMasterPriority,
  } = useACPP();

  const [priorityDraft, setPriorityDraft] = useState("");

  useEffect(() => {
    setPriorityDraft(schoolMaster.priority || "");
  }, [schoolMaster.priority]);

  const preview = useMemo(() => ({
    name: schoolMaster.name || "-",
    board: schoolMaster.board || "-",
    year: schoolMaster.year || "-",
    strength: schoolMaster.strength || "0",
  }), [schoolMaster]);

  const missingLabels = {
    name: "School Name",
    code: "School Code",
    year: "Academic Year",
    board: "Board",
    strength: "School Strength",
    principal: "Principal Name",
    email: "School Email",
    phone: "School Phone",
    vision: "Vision",
  };

  const handleSave = async () => {
    if (!canEditModules1to5) { return; }
    try {
      await saveSchoolMasterPriority(priorityDraft);
      alert("Module 1 priority saved successfully.");
    } catch (error) {
      alert(error?.message || "Failed to save Module 1 priority.");
    }
  };

  const handleReset = async () => {
    if (!canEditModules1to5) { return; }
    try {
      await resetSchoolMasterPriority();
      setPriorityDraft("");
      alert("Module 1 priority reset successfully.");
    } catch (error) {
      alert(error?.message || "Failed to reset Module 1 priority.");
    }
  };

  return (
    <ModuleCard id="module1">
      <section className="mb-6">
        <div className="text-center px-3 py-2">
          <h3 className="text-4xl font-bold text-[#D67C2C] mb-3">School Master Details</h3>
          <p className="text-[#1D3469] text-lg font-medium max-w-4xl mx-auto leading-[1.45]">
            SDP-sourced institutional baseline with ACPP planning context for the active year.
          </p>
        </div>
      </section>

      <ModuleHeader
        step="Module 1"
        title="School Master Details"
        description="This module is sourced from SDP. Core identity and school profile fields are read-only here; only ACPP-specific planning priority is editable."
        showStatus={false}
      />

      {module1LoadError && (
        <SoftPanel title="Sync Warning" className="mb-4">
          <p className="text-[13px] text-[#b63a3a] leading-[1.7] m-0">{module1LoadError}</p>
        </SoftPanel>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard label="School Name" value={preview.name} meta="From SDP School Information" />
        <MetricCard label="Board" value={preview.board} meta="From SDP School Information" />
        <MetricCard label="Academic Year" value={preview.year} meta="From SDP School Information" />
        <MetricCard label="School Strength" value={preview.strength} meta="From SDP School Information" />
        <MetricCard label="Status" value={module1Loading ? "Loading..." : (schoolMaster.status || "Pending")} />
      </div>

      <SectionDivider />

      <Grid2>
        <SoftPanel title="Institution Identity (SDP-sourced)">
          <Grid2>
            <div>
              <InputLabel htmlFor="schoolName">School Name</InputLabel>
              <FormInput id="schoolName" value={schoolMaster.name || ""} readOnly />
              <HelpText>Source: SDP &gt; School Information</HelpText>
            </div>
            <div>
              <InputLabel htmlFor="schoolCode">School Code</InputLabel>
              <FormInput id="schoolCode" value={schoolMaster.code || ""} readOnly />
              <HelpText>Source: SDP &gt; School Information</HelpText>
            </div>
            <div>
              <InputLabel htmlFor="academicYear">Academic Year</InputLabel>
              <FormInput id="academicYear" value={schoolMaster.year || ""} readOnly />
              <HelpText>Source: SDP &gt; School Information</HelpText>
            </div>
            <div>
              <InputLabel htmlFor="schoolBoard">Board</InputLabel>
              <FormInput id="schoolBoard" value={schoolMaster.board || ""} readOnly />
              <HelpText>Source: SDP &gt; School Information</HelpText>
            </div>
          </Grid2>
        </SoftPanel>

        <SoftPanel title="Leadership Context (SDP-sourced)">
          <Grid2>
            <div>
              <InputLabel htmlFor="schoolStrength">Total School Strength</InputLabel>
              <FormInput id="schoolStrength" value={schoolMaster.strength || ""} readOnly />
              <HelpText>Source: SDP &gt; School Information</HelpText>
            </div>
            <div>
              <InputLabel htmlFor="principalName">Principal / Head Name</InputLabel>
              <FormInput id="principalName" value={schoolMaster.principal || ""} readOnly />
              <HelpText>Source: SDP &gt; School Information</HelpText>
            </div>
            <div>
              <InputLabel htmlFor="schoolEmail">School Email</InputLabel>
              <div className={`rounded-xl border px-3 py-2 text-[13px] bg-white ${!schoolMaster.email ? "border-amber-300 bg-amber-50" : "border-[#d8e4f5]"}`}>
                {schoolMaster.email || <span className="text-amber-600 italic">Not set</span>}
              </div>
              {!schoolMaster.email ? (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-[11px] text-amber-600 font-medium">Missing —</span>
                  <button
                    type="button"
                    onClick={() => { window.location.href = "/sdp/school-information"; }}
                    className="text-[11px] text-[#D67C2C] underline font-semibold hover:text-[#c06928] cursor-pointer"
                  >
                    Add in SDP › School Information
                  </button>
                </div>
              ) : (
                <HelpText>Source: SDP &gt; School Information</HelpText>
              )}
            </div>
            <div>
              <InputLabel htmlFor="schoolPhone">School Contact Number</InputLabel>
              <div className={`rounded-xl border px-3 py-2 text-[13px] bg-white ${!schoolMaster.phone ? "border-amber-300 bg-amber-50" : "border-[#d8e4f5]"}`}>
                {schoolMaster.phone || <span className="text-amber-600 italic">Not set</span>}
              </div>
              {!schoolMaster.phone ? (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-[11px] text-amber-600 font-medium">Missing —</span>
                  <button
                    type="button"
                    onClick={() => { window.location.href = "/sdp/school-information"; }}
                    className="text-[11px] text-[#D67C2C] underline font-semibold hover:text-[#c06928] cursor-pointer"
                  >
                    Add in SDP › School Information
                  </button>
                </div>
              ) : (
                <HelpText>Source: SDP &gt; School Information</HelpText>
              )}
            </div>
          </Grid2>
        </SoftPanel>
      </Grid2>

      <SectionDivider />

      <Grid2>
        <SoftPanel title="Academic Vision Context (SDP-sourced)">
          <InputLabel htmlFor="schoolVisionLine">School Academic Direction</InputLabel>
          <FormTextarea id="schoolVisionLine" value={schoolMaster.vision || ""} disabled />
          <HelpText>Source: SDP &gt; Institutional Identity (Vision)</HelpText>
        </SoftPanel>
        {canEditModules1to5 ? (
          <SoftPanel title="Institutional Planning Context (ACPP extra)">
            <InputLabel htmlFor="planningPriority">Top Planning Priority</InputLabel>
            <FormTextarea
              id="planningPriority"
              value={priorityDraft}
              onChange={(e) => setPriorityDraft(e.target.value)}
              disabled={!canEditModules1to5}
              placeholder="What is the main planning priority this year?"
            />
            <HelpText>This is the only Module 1 field stored in ACPP extras.</HelpText>
          </SoftPanel>
        ) : (
          <SoftPanel title="Institutional Planning Context (ACPP extra)">
            <InputLabel htmlFor="planningPriorityReadonly">Top Planning Priority</InputLabel>
            <FormTextarea id="planningPriorityReadonly" value={schoolMaster.priority || "—"} disabled />
            <HelpText>Saved ACPP planning priority (read-only).</HelpText>
          </SoftPanel>
        )}
      </Grid2>

      <SectionDivider />

      <Grid3>
        <SoftPanel title="Missing SDP Fields">
          <div className="flex flex-wrap gap-2">
            {module1MissingFields.length
              ? module1MissingFields.map((field) => (
                  <Tag key={field} color="yellow">{missingLabels[field] || field}</Tag>
                ))
              : <Tag color="green">All required sourced fields available</Tag>}
          </div>
        </SoftPanel>
        <SoftPanel title="Update Institutional Identity">
          <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">
            Vision and identity content is managed in SDP.
          </p>
          <BtnLight onClick={() => { window.location.href = "/sdp/institutional-identity"; }}>
            Edit in SDP Institutional Identity
          </BtnLight>
        </SoftPanel>
        <SoftPanel title="Update School Profile">
          <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">
            School profile, contacts, and academic year are managed in SDP.
          </p>
          <BtnLight onClick={() => { window.location.href = "/sdp/school-information"; }}>
            Edit in SDP School Information
          </BtnLight>
        </SoftPanel>
      </Grid3>

      {canEditModules1to5 ? (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4">
          <BtnRow>
          <BtnLight onClick={loadSchoolMasterFromSources}>Refresh from SDP</BtnLight>
          <BtnPrimary onClick={handleSave} disabled={!canEditModules1to5}>Save ACPP Priority</BtnPrimary>
          </BtnRow>
        </div>
      ) : null}
    </ModuleCard>
  );
}
